/**
 * Cart drawer "You might like" slider.
 *
 * The element lives inside #MinimogCartDrawerFooter, which cart.js replaces
 * wholesale on every cart update. Responses are cached per recommendations URL
 * *and* cart signature, so those re-renders paint from memory instead of
 * re-hitting the network, without serving slides the cart has since absorbed.
 */
(() => {
  if (customElements.get('cart-drawer-recommendations')) return;

  const cache = new Map();

  // Cap the seed walk so a large cart cannot fan out into many requests.
  const MAX_SEEDS = 4;

  class CartDrawerRecommendations extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-rec-viewport]');
      this.track = this.querySelector('[data-rec-track]');
      this.dotsEl = this.querySelector('[data-rec-dots]');

      if (!this.viewport || !this.track) return;

      this.onScroll = this.debounce(this.updateActiveDot.bind(this), 60);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('click', this.onClick.bind(this));

      // Liquid has already rendered products into the track, so the slider is
      // live before this script does anything. Keep them as the baseline: the
      // API result replaces them only if it actually returns something.
      this.fallbackHTML = this.track.innerHTML.trim();

      if (this.fallbackHTML) this.buildSlides();

      // Loaded eagerly on purpose. Lazy-loading via IntersectionObserver does
      // not work here: the element lives inside an off-canvas drawer that is
      // hidden until opened, so it may never intersect and the slider would
      // stay empty forever. The request is one small cached fetch.
      this.load();
    }

    buildSlides() {
      this.slides = Array.from(this.track.querySelectorAll('.cd-rec__slide'));
      this.buildDots();
    }

    disconnectedCallback() {
      if (this.viewport && this.onScroll) this.viewport.removeEventListener('scroll', this.onScroll);
    }

    /**
     * Every product in the cart, in cart order. Recommendations are configured
     * per product, so any one of them may be the product that actually has some.
     */
    get seedIds() {
      // `data-product-id` is the single-seed attribute older markup shipped
      // with. Reading both keeps a half-deployed theme (new snippet + cached
      // old script, or the reverse) working instead of silently rendering
      // nothing.
      const ids = (this.dataset.seedIds || this.dataset.productId || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      return [...new Set(ids)].slice(0, MAX_SEEDS);
    }

    urlFor(productId) {
      const base = this.dataset.recommendationsUrl || '/recommendations/products';
      const limit = this.dataset.limit || 6;
      const intent = this.dataset.intent || 'related';
      return `${base}?section_id=cart-drawer-recommendations&product_id=${productId}&limit=${limit}&intent=${intent}`;
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
      };
    }

    /**
     * Try each cart product as a seed, stopping at the first that returns
     * results. In practice that is one request — the loop only continues when a
     * product has no recommendations configured.
     */
    async load() {
      const seeds = this.seedIds;

      if (!seeds.length) {
        console.warn(
          '[cart-drawer-recommendations] No seed product ids on the element — nothing to request. ' +
            'The snippet and the script are probably out of step on the store; re-deploy the theme.'
        );
        this.render(this.fallbackHTML);
        return;
      }

      // The section filters out products already in the cart, so a response is
      // only reusable while the cart holds the same products. Keying on the URL
      // alone served stale slides after every add or remove.
      const cartSignature = seeds.join(',');

      for (const productId of seeds) {
        const url = this.urlFor(productId);
        const key = `${url}|${cartSignature}`;

        let html = cache.get(key);
        if (html === undefined) {
          html = await this.fetchSlides(url);
          // Only cache a definitive answer; a network failure should be retried.
          if (html !== null) cache.set(key, html);
        }

        if (html) {
          this.render(html);
          return;
        }
      }

      if (!this.fallbackHTML) {
        console.warn(
          `[cart-drawer-recommendations] Shopify returned no recommendations for ${seeds.join(
            ', '
          )} (intent: ${this.dataset.intent || 'related'}), and no fallback is configured — hiding the slider.`
        );
      }

      this.render(this.fallbackHTML);
    }

    /** Returns slide HTML, '' when Shopify has no recommendations, null on failure. */
    async fetchSlides(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
        const text = await response.text();
        const slides = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-rec-slides]');
        return slides ? slides.innerHTML.trim() : '';
      } catch (error) {
        console.error('[cart-drawer-recommendations]', error);
        return null;
      }
    }

    render(html) {
      if (!html) {
        this.hidden = true;
        return;
      }

      // Avoid a visible swap when the API returns what is already on screen.
      if (html !== this.track.innerHTML.trim()) this.track.innerHTML = html;

      this.slides = Array.from(this.track.querySelectorAll('.cd-rec__slide'));

      if (!this.slides.length) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.buildDots();
    }

    buildDots() {
      if (!this.dotsEl) return;

      // A single slide has nothing to page through.
      if (this.slides.length < 2) {
        this.dotsEl.innerHTML = '';
        this.dotsEl.hidden = true;
        return;
      }

      this.dotsEl.hidden = false;
      this.dotsEl.innerHTML = this.slides
        .map(
          (_, index) =>
            `<button type="button" class="cd-rec__dot${index === 0 ? ' is-active' : ''}" role="tab" aria-label="${
              index + 1
            }" data-rec-dot="${index}"></button>`
        )
        .join('');
    }

    activeIndex() {
      if (!this.slides || !this.slides.length) return 0;
      const width = this.slides[0].offsetWidth || 1;
      return Math.min(this.slides.length - 1, Math.round(this.viewport.scrollLeft / width));
    }

    updateActiveDot() {
      if (!this.dotsEl) return;
      const active = this.activeIndex();
      this.dotsEl.querySelectorAll('[data-rec-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', Number(dot.dataset.recDot) === active);
      });
    }

    goTo(index) {
      if (!this.slides || !this.slides[index]) return;
      this.viewport.scrollTo({ left: this.slides[index].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }

    onClick(event) {
      const dot = event.target.closest('[data-rec-dot]');
      if (dot) {
        this.goTo(Number(dot.dataset.recDot));
        return;
      }

      const quickAdd = event.target.closest('[data-quick-add]');
      if (quickAdd) {
        event.preventDefault();
        this.quickAdd(quickAdd);
      }
    }

    /**
     * Mirrors the add-to-cart flow in theme-global.js: bundle the sections that
     * want re-rendering, POST as form data, then emit `cartUpdate` so the
     * drawer, cart count and any subscribers refresh from the same response.
     */
    async quickAdd(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.classList.contains('is-loading')) return;

      button.classList.add('is-loading');
      button.disabled = true;

      const { MinimogSettings, MinimogTheme, MinimogEvents } = window;

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', sectionsToBundle);
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(MinimogSettings.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const result = await response.json();

        if (result.status) throw new Error(result.description || result.message);

        MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: result, productVariantId: variantId });
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result } }));
      } catch (error) {
        console.error('[cart-drawer-recommendations]', error);
        if (MinimogTheme && MinimogTheme.Notification) {
          MinimogTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'error',
            message: error.message,
            last: 3000,
          });
        }
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }
  }

  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
})();
